import { useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { PhotoIcon, ArrowUpTrayIcon, TrashIcon, LinkIcon } from '@heroicons/react/24/outline'
import { uploadAPI } from '../services/api'
import { t } from '../i18n'

const TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif', 'image/svg+xml']

// Business logo / profile picture: upload a file from the computer or phone
// (or paste a link). The logo shows in the website header of every template.
export default function LogoUploadField({ value, onChange, label = 'Logo' }) {
  const inputRef = useRef(null)
  const [uploading, setUploading] = useState(false)
  const [showUrl, setShowUrl] = useState(false)

  const upload = async (file) => {
    if (!file) return
    if (!TYPES.includes(file.type)) return toast.error(t('Please select a valid image file (JPEG, PNG, WebP, GIF)'))
    if (file.size > 5 * 1024 * 1024) return toast.error(t('Image size must be less than 5MB'))
    setUploading(true)
    try {
      const form = new FormData()
      form.append('image', file)
      const { data } = await uploadAPI.uploadImage(form)
      onChange(data.imageUrl)
      toast.success(t('Logo uploaded - remember to save'))
    } catch (err) {
      toast.error(t(err.response?.data?.error || 'Image upload failed'))
    } finally {
      setUploading(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  return (
    <div>
      <label className="form-label">{t(label)}</label>
      <div className="flex flex-wrap items-center gap-5 mt-1">
        <div
          onDragOver={e => e.preventDefault()}
          onDrop={e => { e.preventDefault(); upload(e.dataTransfer.files?.[0]) }}
          className="h-24 w-24 rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50 grid place-items-center overflow-hidden flex-shrink-0"
        >
          {value
            ? <img src={value} alt={t('Logo Preview')} className="h-full w-full object-contain p-1.5" onError={e => { e.currentTarget.style.visibility = 'hidden' }} />
            : <PhotoIcon className="h-8 w-8 text-gray-400" />}
        </div>
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} className="btn-primary inline-flex items-center gap-2">
              <ArrowUpTrayIcon className="h-4 w-4" />{uploading ? t('Uploading...') : value ? t('Change logo') : t('Upload logo')}
            </button>
            {value && (
              <button type="button" onClick={() => onChange('')} className="btn-secondary inline-flex items-center gap-2">
                <TrashIcon className="h-4 w-4" />{t('Remove')}
              </button>
            )}
          </div>
          <p className="text-xs text-gray-500">{t('PNG, JPG, WebP or SVG up to 5MB. A square image works best.')}</p>
          <button type="button" onClick={() => setShowUrl(s => !s)} className="text-xs text-gray-600 inline-flex items-center gap-1 hover:underline">
            <LinkIcon className="h-3.5 w-3.5" />{t('Or paste an image link')}
          </button>
        </div>
      </div>
      {showUrl && (
        <input type="url" value={value || ''} onChange={e => onChange(e.target.value)} className="form-input mt-3" placeholder="https://example.com/logo.png" />
      )}
      <input ref={inputRef} type="file" accept={TYPES.join(',')} className="hidden" onChange={e => upload(e.target.files?.[0])} />
    </div>
  )
}
